import apiClient from './client'
import { Resource, CheckChannelResponse, PricingTiersResponse } from '../types'

export const resourcesApi = {
  // List resources
  listResources: async (ownerId?: string): Promise<Resource[]> => {
    const url = ownerId ? `/api/v1/resources?owner_id=${ownerId}` : '/api/v1/resources'
    const response = await apiClient.get(url)
    return response.data.resources || []
  },

  // Get resource by ID
  getResource: async (resourceId: string): Promise<Resource> => {
    const response = await apiClient.get(`/api/v1/resources/${resourceId}`)
    return response.data
  },

  // Create resource
  createResource: async (data: Partial<Resource>): Promise<Resource> => {
    const response = await apiClient.post('/api/v1/resources', data)
    return response.data
  },

  // Update resource
  updateResource: async (resourceId: string, data: Partial<Resource>): Promise<Resource> => {
    const payload = { id: resourceId, ...data }
    const response = await apiClient.put(`/api/v1/resources/${resourceId}`, payload)

    // Handle new response structure with warning (backend returns { resource, warning })
    if (response.data.resource) {
      // Show warning alert if present
      if (response.data.warning && window.Telegram?.WebApp) {
        window.Telegram.WebApp.showAlert(response.data.warning)
      }
      return response.data.resource
    }

    // Fallback for backward compatibility (in case backend returns direct Resource)
    return response.data
  },

  // Delete resource
  deleteResource: async (resourceId: string): Promise<void> => {
    await apiClient.delete(`/api/v1/resources/${resourceId}`)
  },

  // Update resource status
  updateResourceStatus: async (
    resourceId: string,
    status: 'active' | 'inactive' | 'pending' | 'rejected' | 'maintenance'
  ): Promise<Resource> => {
    const response = await apiClient.put(`/api/v1/resources/${resourceId}/status`, { status })
    return response.data
  },

  // Check if resource exists (channel via link or bot via bot_token)
  checkResource: async (link?: string, botToken?: string): Promise<CheckChannelResponse> => {
    const payload: { link?: string; bot_token?: string } = {}
    if (link) payload.link = link
    if (botToken) payload.bot_token = botToken
    const response = await apiClient.post('/api/v1/resources/check', payload)
    return response.data
  },

  // Get pricing tiers for resource
  getPricingTiers: async (resourceId: string): Promise<PricingTiersResponse> => {
    console.log('[ResourcesAPI] Fetching pricing tiers:', resourceId)
    const response = await apiClient.get(`/api/v1/resources/${resourceId}/pricing-tiers`)
    return response.data
  },

  // Change resource price
  changeResourcePrice: async (resourceId: string, price: number): Promise<Resource> => {
    console.log('[ResourcesAPI] Changing resource price:', resourceId, price)
    const response = await apiClient.put(`/api/v1/resources/${resourceId}/price`, { price })
    return response.data
  },

  // Toggle resource active/inactive
  toggleResource: async (resourceId: string, newStatus: 'active' | 'inactive'): Promise<Resource> => {
    console.log('[ResourcesAPI] Updating resource status:', resourceId, newStatus)
    const response = await apiClient.put(`/api/v1/resources/${resourceId}/status`, { status: newStatus })
    return response.data
  },

  // Whitelist management
  getWhitelistedBots: async (resourceId: string): Promise<string[]> => {
    const response = await apiClient.get(`/api/v1/resources/${resourceId}/whitelisted_bots`)
    return response.data.bot_ids || []
  },

  setWhitelistedBots: async (resourceId: string, botIds: string[]): Promise<void> => {
    await apiClient.put(`/api/v1/resources/${resourceId}/whitelisted_bots`, {
      bot_ids: botIds,
    })
  },

  // Auto-resource toggle
  toggleAutoResource: async (resourceId: string, autoResource: boolean): Promise<Resource> => {
    const response = await apiClient.put(`/api/v1/resources/${resourceId}`, {
      auto_resource: autoResource,
    })
    return response.data
  },
}
